import { Colors, Icon, Text } from '@amelio/ui-library';

import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { Frequency } from '@/constants/Frequency';
import { useAmelioTranslations } from '@/hooks';
import { usePerformanceTier } from '@/hooks/performance-tier/usePerformanceTier';
import { useProGate } from '@/hooks/performance-tier/useProGate';
import { groupIncludesEvaluationType } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { ProGateOutcome } from '@/utils/performanceTier';

interface ShowsManagerAssessmentRemovedParams {
    group: CycleEvaluationGroup | undefined;
    isPro: boolean;
    /** Whether the reader is shown Performance PRO locks: administrators and HR. */
    isShownLocks: boolean;
}

/**
 * Whether a cycle's next occurrences launch without their Manager Assessment: a recurring cycle that
 * pairs one with Self-Assessment or 360 Feedback, in a company that no longer has Performance PRO.
 * The server then drops that step and launches the others as planned; the administrators are told.
 */
export const showsManagerAssessmentRemoved = ({
    group,
    isPro,
    isShownLocks,
}: ShowsManagerAssessmentRemovedParams): boolean =>
    !isPro &&
    isShownLocks &&
    !!group?.frequency &&
    group.frequency !== Frequency.UPON_REQUEST &&
    groupIncludesEvaluationType(group, EvaluationType.MANAGER_EVALUATION) &&
    (groupIncludesEvaluationType(group, EvaluationType.SELF_EVALUATION) ||
        groupIncludesEvaluationType(group, EvaluationType.EVALUATION_360));

interface ManagerAssessmentRemovedNoticeProps {
    group: CycleEvaluationGroup | undefined;
}

/** Tells administrators, on each step of the cycle, that its Manager Assessment step was dropped. */
export const ManagerAssessmentRemovedNotice = ({ group }: ManagerAssessmentRemovedNoticeProps) => {
    const { t } = useAmelioTranslations();
    const { isPro } = usePerformanceTier();
    const gate = useProGate();

    if (
        !showsManagerAssessmentRemoved({ group, isPro, isShownLocks: gate === ProGateOutcome.Lock })
    ) {
        return null;
    }

    return (
        <div
            role={'status'}
            data-testid={'manager-assessment-removed-notice'}
            className={
                'border-warning/50 bg-warning/10 mt-[16px] flex max-w-prose items-start gap-2 rounded-lg border px-4 py-3'
            }
        >
            <Icon
                name={'essential-warning-2'}
                size={'sm'}
                color={Colors.WARNING}
                className={'mt-0.5 shrink-0'}
            />

            <Text size={'small'}>{t('cycleEvaluationGroupStepper.managerAssessmentRemoved')}</Text>
        </div>
    );
};
